import { useApolloClient } from '@apollo/client';
import { Button, Form as AntdForm, message, Modal, Popconfirm, Space, Tabs } from 'antd';
import { Formik } from 'formik';
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type InventoryDetailsWithPermissionsDataFragment } from '../../../api/fragments/InventoryDetailsWithPermissionsData';
import {
    AddStockInventoryDocument,
    type AddStockInventoryMutation,
    type AddStockInventoryMutationVariables,
} from '../../../api/mutations/addStockInventory';
import {
    TakeOutStockInventoryDocument,
    type TakeOutStockInventoryMutation,
    type TakeOutStockInventoryMutationVariables,
} from '../../../api/mutations/takeOutStockInventory';
import { useUpdateInventoryMutation } from '../../../api/mutations/updateInventory';
import { GetInventoryDocument } from '../../../api/queries/getInventory';
import { type InventoryUpdationSettings } from '../../../api/types';
import Form from '../../../components/fields/Form';
import FormItem from '../../../components/fields/FormItem';
import { InputNumber } from '../../../components/fields/InputNumberField';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import useTranslatedString from '../../../utilities/useTranslatedString';
// eslint-disable-next-line max-len
import InventoryActivityLogWithComment from '../../shared/ActivityLog/ActivityLogWithComment/InventoryActivityLogWithComment';
import InventoryDetailsForm from './InventoryDetailsForm';
import { HeaderTabs } from './shared';

type StockFormPopupProps = {
    label: string;
    value: number;
    onChange: (newValue: number) => void;
};

const stockValidation = { min: 1, max: 999 };

const stockFieldStyle = { style: { width: 220 } };

const StockFormPopup = ({ label, value, onChange }: StockFormPopupProps) => {
    const { t } = useTranslation(['common']);
    const [, startTransition] = useTransition();

    const [meta, setMeta] = useState<{
        touched: boolean;
        error: string | null;
        initialValue?: number;
        value: number;
    }>({
        touched: false,
        error: null,
        value,
    });

    useEffect(() => {
        // Delaying validation to prevent jitter
        startTransition(() => {
            let error = null;
            if (value > stockValidation.max) {
                error = t('common:formErrors.numberBetween', stockValidation);
            }

            if (value < stockValidation.min) {
                error = t('common:formErrors.numberBetween', stockValidation);
            }

            setMeta({ error, touched: true, value });
        });
    }, [t, value]);

    return (
        <AntdForm layout="inline">
            <FormItem label={label} meta={meta} wrapperCol={stockFieldStyle}>
                <InputNumber onChange={onChange} style={{ width: '100%' }} value={value} />
            </FormItem>
        </AntdForm>
    );
};

const AddStockPopConfirm = ({ inventoryId }: { inventoryId: string }) => {
    const { t } = useTranslation(['inventoryDetails']);
    const [value, setValue] = useState(1);

    const client = useApolloClient();

    const handleSubmit = useCallback(async () => {
        message.loading({
            content: t('inventoryDetails:messages.addStock.submitting'),
            key: 'primary',
            duration: 0,
        });

        await client
            .mutate<AddStockInventoryMutation, AddStockInventoryMutationVariables>({
                mutation: AddStockInventoryDocument,
                variables: {
                    inventoryId,
                    value,
                },
                refetchQueries: [GetInventoryDocument],
            })
            .finally(() => {
                message.destroy('primary');
            });

        message.success({
            content: t('inventoryDetails:messages.addStock.success'),
            key: 'primary',
        });
    }, [client, inventoryId, t, value]);

    return (
        <Popconfirm
            cancelText={t('inventoryDetails:actions.cancel')}
            okButtonProps={{
                disabled: value < stockValidation.min || value > stockValidation.max,
            }}
            okText={t('inventoryDetails:actions.addStocksConfirm')}
            onCancel={() => setValue(1)}
            onConfirm={handleSubmit}
            placement="topRight"
            title={
                <StockFormPopup
                    label={t('inventoryDetails:messages.addStock.title')}
                    onChange={setValue}
                    value={value}
                />
            }
        >
            <Button>{t('inventoryDetails:actions.addStocks')}</Button>
        </Popconfirm>
    );
};

const DeleteStockPopConfirm = ({ inventoryId, availableStock }: { inventoryId: string; availableStock: number }) => {
    const { t } = useTranslation(['inventoryDetails']);
    const [value, setValue] = useState(1);

    const client = useApolloClient();

    const deleteStocks = useCallback(async () => {
        message.loading({
            content: t('inventoryDetails:messages.deleteStock.submitting'),
            key: 'primary',
            duration: 0,
        });

        await client
            .mutate<TakeOutStockInventoryMutation, TakeOutStockInventoryMutationVariables>({
                mutation: TakeOutStockInventoryDocument,
                variables: {
                    inventoryId,
                    value,
                },
                refetchQueries: [GetInventoryDocument],
            })
            .finally(() => {
                message.destroy('primary');
            });

        message.success({
            content: t('inventoryDetails:messages.deleteStock.success'),
            key: 'primary',
        });
    }, [client, inventoryId, t, value]);

    const handleSubmit = useCallback(() => {
        if (value > availableStock) {
            Modal.confirm({
                type: 'error',
                title: t('inventoryDetails:messages.unavailableDeleteStock.title'),
                content: t('inventoryDetails:messages.unavailableDeleteStock.content'),
                cancelButtonProps: {
                    style: {
                        display: 'none',
                    },
                },
            });

            return;
        }

        deleteStocks();
    }, [availableStock, deleteStocks, t, value]);

    return (
        <Popconfirm
            cancelText={t('inventoryDetails:actions.cancel')}
            okButtonProps={{
                disabled: value < stockValidation.min || value > stockValidation.max,
            }}
            okText={t('inventoryDetails:actions.delete')}
            onCancel={() => setValue(1)}
            onConfirm={handleSubmit}
            title={
                <StockFormPopup
                    label={t('inventoryDetails:messages.deleteStock.title')}
                    onChange={setValue}
                    value={value}
                />
            }
        >
            <Button danger>{t('inventoryDetails:actions.deleteStocks')}</Button>
        </Popconfirm>
    );
};

const InventoryDetailsInnerPage = ({ inventory }: { inventory: InventoryDetailsWithPermissionsDataFragment }) => {
    const { t } = useTranslation(['inventoryDetails', 'common']);
    const translate = useTranslatedString();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: inventory.versioning,
        timeZone: inventory.module.company.timeZone,
    });

    const navigate = useNavigate();
    const location = useLocation();

    const [activeTab, setActiveTab] = useState(HeaderTabs.Details);

    const onChangeTab = useCallback((activeKey: HeaderTabs) => {
        setActiveTab(activeKey);
    }, []);

    const [updateInventoryMutation] = useUpdateInventoryMutation();
    const handleUpdateInventory = useHandleError(
        async (values: InventoryUpdationSettings) => {
            message.loading({
                content: t('inventoryDetails:messages.updateInventory.submitting'),
                key: 'primary',
                duration: 0,
            });

            await updateInventoryMutation({
                variables: {
                    id: inventory.id,
                    settings: {
                        isActive: values.isActive,
                        period: values.period,
                    },
                },
            }).finally(() => {
                message.destroy('primary');
            });

            message.success({
                content: t('inventoryDetails:messages.updateInventory.success'),
                key: 'primary',
            });
        },
        [inventory.id, t, updateInventoryMutation]
    );

    const [hasUpdatePermission, hasDeletePermission] = useMemo(
        () => [
            hasPermissions(inventory.permissions, [permissionKind.updateInventory]),
            hasPermissions(inventory.permissions, [permissionKind.deleteInventory]),
        ],
        [inventory.permissions]
    );

    const hasFooter = hasUpdatePermission || hasDeletePermission;
    const footer = (
        <Space key="footer" size={8}>
            {hasDeletePermission ? (
                <DeleteStockPopConfirm
                    key="delete"
                    availableStock={inventory.availableStock}
                    inventoryId={inventory.id}
                />
            ) : null}
            {hasUpdatePermission ? (
                <>
                    <AddStockPopConfirm key="addStock" inventoryId={inventory.id} />
                    <Button key="save" form="inventoryDetails" htmlType="submit" type="primary">
                        {t('inventoryDetails:actions.save')}
                    </Button>
                </>
            ) : null}
        </Space>
    );

    const renderActiveTab = () => {
        switch (activeTab) {
            case HeaderTabs.Details: {
                return (
                    <Formik
                        initialValues={{
                            ...inventory,
                            lastModified: updated,
                            moduleName: inventory.module.displayName,
                            variantName: translate(inventory.variant.name),
                            modelName: translate(inventory.model.name),
                            subModelName: inventory.subModel?.name ? translate(inventory.subModel?.name) : '-',
                            dealerName: inventory.dealer.displayName,
                            packageSetting:
                                inventory.__typename === 'ConfiguratorInventory'
                                    ? {
                                          ...inventory.packageSetting,
                                          packageName: inventory.packageSetting?.packageName ?? '-',
                                      }
                                    : undefined,
                        }}
                        onSubmit={handleUpdateInventory}
                    >
                        {({ handleSubmit }) => (
                            <Form id="inventoryDetails" name="inventoryDetails" onSubmitCapture={handleSubmit}>
                                <InventoryDetailsForm
                                    disabled={!hasUpdatePermission}
                                    offset={offset}
                                    permissions={{ hasDeletePermission }}
                                />
                            </Form>
                        )}
                    </Formik>
                );
            }

            case HeaderTabs.ActivityLog:
                return (
                    <InventoryActivityLogWithComment
                        inventoryId={inventory.id}
                        timezone={inventory.module.company.timeZone}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <ConsolePageWithHeader
            footer={hasFooter ? [footer] : null}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={onChangeTab}>
                        <Tabs.TabPane key={HeaderTabs.Details} tab={t('inventoryDetails:tabs.details')} />
                        <Tabs.TabPane key={HeaderTabs.ActivityLog} tab={t('inventoryDetails:tabs.activityLog')} />
                    </Tabs>
                ),
            }}
            onBack={() => {
                navigate(
                    location.state?.currentPage === 'modelConfigurator'
                        ? `/admin/configurators/${location.state.modelConfiguratorId}`
                        : `/admin/inventories/`,
                    { state: location.state }
                );
            }}
            title={t('inventoryDetails:titles.details')}
        >
            {renderActiveTab()}
        </ConsolePageWithHeader>
    );
};

export default InventoryDetailsInnerPage;
